import {
  Component,
  ChangeDetectorRef,
  EventEmitter,
  Input,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormGroup, FormControl, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { ColumnMode, SortType, SelectionType } from '@siemens/ngx-datatable';
import { StatusType } from '@siemens/element-ng/common';
import { SI_DATATABLE_CONFIG } from '@siemens/element-ng/datatable';
import { Link } from '@siemens/element-ng/link';
import { ModalRef } from '@siemens/element-ng/modal';
import { SelectOption } from '@siemens/element-ng/select';
import { SiToastNotificationService } from '@siemens/element-ng/toast-notification';
import {
  switchMap,
  throwError,
  of,
  retryWhen,
  scan,
  delay,
  catchError,
  timeout,
  finalize,
} from 'rxjs';
import { CONSTANTS, NOTIFICATIONS } from 'src/app/constants/constant';
import {
  CloudAccount,
  CloudAccountInput,
  ClusterInput,
} from 'src/app/models/cloudAccount';
import { Project, ProjectInput, Cluster } from 'src/app/models/project';
import { User } from 'src/app/models/user';
import { CloudAccountService } from 'src/app/services/cloud-account.service';
import { ClusterService } from 'src/app/services/cluster.service';
import { ConfigService } from 'src/app/services/config.service';
import { ProjectService } from 'src/app/services/project.service';
import { UserService } from 'src/app/services/user.service';

type ToastStateName = StatusType;

// Custom validator to prevent project names starting with "SAG"
function preventSagPrefixValidator(control: AbstractControl): ValidationErrors | null {
  if (!control.value) {
    return null;
  }
  
  const projectName = control.value.toString().trim().toUpperCase();
  if (projectName.startsWith('SAG')) {
    return { 'sagPrefix': { value: control.value } };
  }
  
  return null;
}

@Component({
  selector: 'app-create-project',
  templateUrl: './create-project.component.html',
  styleUrl: './create-project.component.scss',
  standalone: false,
})
export class CreateProjectComponent {
  @ViewChild('template') createEditTemplate!: TemplateRef<any>;
  @Input() isEditingProject: any;
  @Input() initialSubscriptionCount: number = 0; // Total count from side panel
  @Input() initialClusterCount: number = 0; // Total count from side panel
  @Input() folderList: SelectOption<string>[] = [];
  @Input() selectedProject: Project | undefined;
  @Output() onSave = new EventEmitter<any>();
  @Output() onClose = new EventEmitter<any>();

  private ref?: ModalRef<unknown>;

  constants = CONSTANTS;
  clusterSubsColumns: any = [];
  clusPageNumber = 0;
  ColumnMode = ColumnMode;
  createProjectForm: any;
  cloudAccounts = [];
  clusterRows: any = [];
  clusterAccounts = [];
  currentUser: User | undefined;
  isAddingOwner = false;
  isLoading = false;
  isLoadingSubscriptions = false;
  isLoadingClusters = false;
  isSearchingSubscriptions = false;
  isSearchingClusters = false;
  isFolderProject = false;
  isInitializingSelection = false; // Flag to prevent select handler from overwriting initial selection
  labelWidth = 140;
  labelWidthDummy = 50;
  newProjectOwners = [''];
  notificationConstants = NOTIFICATIONS;
  notSelectedSubscriptions: any[] = [];
  notSelectedClusters: any[] = [];
  pageNumber = 0;
  pageSize = 10;
  selectedFolder = 'Root';
  selectedSubscriptions: any[] = [];
  selectedClusters: any[] = [];
  showSubscriptionsTable = false;
  showClustersTable = false;
  subscriptionSearchTerm = '';
  clusterSearchTerm = '';
  showOnlySelectedSubscriptions = false;
  showOnlySelectedClusters = false;
  subscriptionTotalCount = 0;
  clusterTotalCount = 0;
  SortType = SortType;
  subsColumns: any = [];
  subsPageNumber = 0;
  subscriptionRows: any = [];
  subsSelectionType = SelectionType.checkbox;
  tableConfig = SI_DATATABLE_CONFIG;
  entraGroupObjectId = '';
  tempSubRows: any = [];
  tempClusterRows: any = [];
  totalSubscriptions = 0;
  totalClusters = 0;
  
  // Cache for pagination - stores pages by page number
  private subscriptionPageCache = new Map<number, any[]>();
  private clusterPageCache = new Map<number, any[]>();

  private normalizeListItem(item: any): any {
    return {
      ...item,
      id: item?.id ?? item?.Id,
      name: item?.name ?? item?.Name ?? '',
      externalId: item?.externalId ?? item?.ExternalId ?? '',
    };
  }

  private setSubscriptionData(items: any[], totalCount: number, cachePageZero: boolean = false) {
    this.subscriptionRows = items;
    this.tempSubRows = items;
    this.subscriptionTotalCount = totalCount;
    this.totalSubscriptions = totalCount;
    if (cachePageZero) {
      this.subscriptionPageCache.set(0, items);
    }
    this.showSubscriptionsTable = true;
    this.refreshView();
  }

  private setClusterData(items: any[], totalCount: number, cachePageZero: boolean = false) {
    this.clusterRows = items;
    this.tempClusterRows = items;
    this.clusterTotalCount = totalCount;
    this.totalClusters = totalCount;
    if (cachePageZero) {
      this.clusterPageCache.set(0, items);
    }
    this.showClustersTable = true;
    this.refreshView();
  }

  constructor(
    private userService: UserService,
    private configService: ConfigService,
    private clusterService: ClusterService,
    private cloudAccountService: CloudAccountService,
    private projectService: ProjectService,
    private toastNotificationService: SiToastNotificationService,
    private cdr: ChangeDetectorRef
  ) {
    this.createProjectForm = this.buildForm();
  }

  private refreshView() {
    this.cdr.detectChanges();
  }

  ngOnInit() {
    this.initSubTableColumns();
    this.initClusterSubTableColumns();
    this.currentUser = this.configService.currentUser;
    
    setTimeout(() => {
      if (this.isEditingProject) {
        this.editProject();
      } else {
        this.createProject();
      }
    }, 0);
  }

  private initSubTableColumns(): void {
    this.subsColumns = [
      {
        prop: 'name',
        name: 'Name',
        minWidth: 30,
        maxWidth: 280,
        resizeable: true,
        canAutoResize: true,
        checkboxable: true,
        sortable: true,
        headerCheckboxable: false,
      },
      {
        prop: 'externalId',
        name: 'Subscription Id',
        minWidth: 40,
        maxWidth: 300,
        resizeable: true,
        sortable: true,
        canAutoResize: true,
      },
    ];
  }

  private initClusterSubTableColumns(): void {
    this.clusterSubsColumns = [
      {
        prop: 'checkbox',
        name: '',
        width: 50,
        headerCheckboxable: true,
        checkboxable: true,
        sortable: false,
        canAutoResize: false,
      },
      {
        prop: 'name',
        name: 'Name',
        minWidth: 30,
        maxWidth: 250,
        resizeable: true,
        canAutoResize: true,
        sortable: true,
        headerCheckboxable: false,
      },
      {
        prop: 'externalId',
        name: 'Cluster Id',
        minWidth: 40,
        maxWidth: 300,
        resizeable: true,
        sortable: true,
        canAutoResize: true,
      },
    ];
  }

  public createProject() {
    this.selectedFolder = 'Root';
    this.isEditingProject = false;
    this.isFolderProject = false;
    this.selectedSubscriptions = [];
    this.selectedClusters = [];
    this.selectedProject = {} as Project;
    this.subscriptionRows = [];
    this.clusterRows = [];
    this.subscriptionSearchTerm = '';
    this.clusterSearchTerm = '';
    this.currentUser = this.configService.currentUser;
    this.newProjectOwners = [''];
    this.newProjectOwners.pop();
    this.newProjectOwners?.push(this.currentUser!.email);
    this.createProjectForm.controls.businessUnit.setValue(
      this.currentUser!.name.substring(
        this.currentUser!.name.indexOf('(') + 1,
        this.currentUser?.name.indexOf(')')
      )
    );
    this.createProjectForm.controls.newProjectOwner.reset();
    
    // Clear pagination cache
    this.subscriptionPageCache.clear();
    this.clusterPageCache.clear();
    this.subsPageNumber = 0;
    this.clusPageNumber = 0;

    // Lazy load first 3 subscriptions and clusters
    this.loadInitialSubscriptions();
    this.loadInitialClusters();

    //this.openModal(this.createEditTemplate, 'modal-lg');
  }

  public editProject() {
    if (!this.selectedProject) {
      this.showToast(
        this.notificationConstants.warning as ToastStateName,
        this.notificationConstants.error,
        'Could not load project details for editing.'
      );
      this.createProject();
      return;
    }

    this.selectedFolder = 'Root';
    this.createProjectForm.reset();
    this.isFolderProject = this.selectedProject!.isFolder;
    this.isEditingProject = true;
    
    // Clear pagination cache for edit mode
    this.subscriptionPageCache.clear();
    this.clusterPageCache.clear();
    this.subsPageNumber = 0;
    this.clusPageNumber = 0;

    if (
      !this.selectedProject?.identifiers?.includes(this.constants.mcsAutomation)
    ) {
      this.createProjectForm.controls.name.setValue(
        this.selectedProject!.name!
      );
      this.createProjectForm.controls.businessUnit.setValue(
        this.selectedProject!.businessUnit!
      );
      // this.createProjectForm.controls.description.setValue(
      //   this.selectedProject!.description
      // );
      let currentOwners = this.selectedProject!.projectOwners.map(
        (owner) => owner.email
      );
      this.newProjectOwners = currentOwners!;
      const existingSubscriptions = Array.isArray(this.selectedProject!.cloudAccountLinks)
        ? [...this.selectedProject!.cloudAccountLinks]
        : [];
      const existingClusters = Array.isArray(this.selectedProject!.kubernetesClusters)
        ? [...this.selectedProject!.kubernetesClusters]
        : [];

      // Show already-known assignments immediately so edit UI never appears empty/blocked.
      this.selectedSubscriptions = [...existingSubscriptions];
      this.selectedClusters = [...existingClusters];
      this.subscriptionRows = [...existingSubscriptions];
      this.tempSubRows = [...existingSubscriptions];
      this.clusterRows = [...existingClusters];
      this.tempClusterRows = [...existingClusters];
      this.totalSubscriptions = existingSubscriptions.length;
      this.subscriptionTotalCount = existingSubscriptions.length;
      this.totalClusters = existingClusters.length;
      this.clusterTotalCount = existingClusters.length;
      this.showSubscriptionsTable = true;
      this.showClustersTable = true;
      if (this.selectedProject!.ancestorProjects!.length > 0) {
        this.selectedFolder = this.selectedProject!.ancestorProjects![0].id;
      }
      
      // Fetch complete project data with timeout; fall back to the already selected project
      // so edit mode never remains blocked by a hanging details call.
      this.isLoadingSubscriptions = true;
      this.isLoadingClusters = true;

      this.projectService
        .getProject(this.selectedProject!.id)
        .pipe(
          timeout(15000),
          catchError(() => {
            this.showToast(
              this.notificationConstants.warning as ToastStateName,
              this.notificationConstants.error,
              'Loading full project details timed out. Using available data.'
            );
            return of(this.selectedProject as Project);
          })
        )
        .subscribe({
          next: (completeProject: Project) => {
            const safeProject = completeProject || (this.selectedProject as Project);
            // Preserve pre-fetched cloudAccountLinks and kubernetesClusters: the
            // /projects/{id} endpoint may return them empty because subscriptions
            // and clusters are fetched via separate endpoints by the parent (side-panel).
            const fallbackLinks = this.selectedProject?.cloudAccountLinks ?? [];
            const fallbackClusters = this.selectedProject?.kubernetesClusters ?? [];
            safeProject.cloudAccountLinks =
              Array.isArray(safeProject.cloudAccountLinks) && safeProject.cloudAccountLinks.length > 0
                ? safeProject.cloudAccountLinks
                : fallbackLinks;
            safeProject.kubernetesClusters =
              Array.isArray(safeProject.kubernetesClusters) && safeProject.kubernetesClusters.length > 0
                ? safeProject.kubernetesClusters
                : fallbackClusters;

            this.selectedProject = safeProject;

            // Load paginated lists and set up selections based on available project data.
            this.loadInitialSubscriptions();
            this.loadInitialClusters();
          },
          error: () => {
            this.isLoadingSubscriptions = false;
            this.isLoadingClusters = false;
          }
        });
      //this.openModal(template, 'modal-lg');
    } else {
      this.showToast(
        this.notificationConstants.warning as ToastStateName,
        this.notificationConstants.projectCannotBeEdited,
        this.notificationConstants.cantEditPersonalProject
      );
    }
  }

  /**
   * Load initial subscriptions (10 items per page with proper server-side pagination)
   * In edit mode, pre-selects items based on assigned subscription IDs
   */
  private loadInitialSubscriptions() {
    this.isLoadingSubscriptions = true;
    if (!this.subscriptionRows?.length) {
      this.showSubscriptionsTable = false;
    }
    
    this.cloudAccountService
      .getCloudAccountsPaginated(0, this.pageSize)
      .pipe(
        timeout(20000),
        finalize(() => {
          this.isLoadingSubscriptions = false;
          this.showSubscriptionsTable = true;
        })
      )
      .subscribe({
      next: (response: any) => {
        let items = (response.items || [])
          .map((item: any) => this.normalizeListItem(item))
          .filter((item: any) => item.name || item.externalId);

        if (items.length === 0) {
          this.loadSubscriptionsFallback();
          return;
        }
        
        // In edit mode, build selection from assigned items FIRST
        if (this.isEditingProject && this.selectedProject?.cloudAccountLinks && this.selectedProject.cloudAccountLinks.length > 0) {
          // Set flag to prevent select handler from overwriting
          this.isInitializingSelection = true;

          // Normalize assigned items — API may return Pascal-cased Id/Name/ExternalId.
          const assignedLinks = this.selectedProject.cloudAccountLinks.map((s: any) =>
            this.normalizeListItem(s)
          );
          const assignedIds = new Set(assignedLinks.map((s: any) => s.id).filter(Boolean));

          // Items in the current page that are assigned: reuse their exact references
          // so ngx-datatable's reference-equality check marks them as checked.
          const assignedInPage = items.filter((item: any) => assignedIds.has(item.id));
          // Items assigned but not yet in this page: keep normalized links for saving.
          const notInPage = assignedLinks.filter((s: any) => !items.some((item: any) => item.id === s.id));
          this.selectedSubscriptions = [...assignedInPage, ...notInPage];

          // Clear flag after a longer delay to allow datatable to fully initialize
          setTimeout(() => {
            this.isInitializingSelection = false;
          }, 1000);
        } else {
          this.selectedSubscriptions = [];
        }

        // Show the paginated items
        this.setSubscriptionData(items, response.totalCount || 0, true);
      },
      error: () => {
        this.loadSubscriptionsFallback();
      }
    });
  }

  /**
   * Load initial clusters (10 items per page with proper server-side pagination)
   * In edit mode, pre-selects items based on assigned cluster IDs
   */
  private loadInitialClusters() {
    this.isLoadingClusters = true;
    if (!this.clusterRows?.length) {
      this.showClustersTable = false;
    }
    
    this.clusterService
      .getClustersPaginated(0, this.pageSize)
      .pipe(
        timeout(20000),
        finalize(() => {
          this.isLoadingClusters = false;
          this.showClustersTable = true;
        })
      )
      .subscribe({
      next: (response: any) => {
        let items = (response.items || [])
          .map((item: any) => this.normalizeListItem(item))
          .filter((item: any) => item.name || item.externalId);

        if (items.length === 0) {
          this.loadClustersFallback();
          return;
        }
        
        // In edit mode, build selection from assigned items FIRST
        if (this.isEditingProject && this.selectedProject?.kubernetesClusters && this.selectedProject.kubernetesClusters.length > 0) {
          // Set flag to prevent select handler from overwriting
          this.isInitializingSelection = true;

          // Normalize assigned items — API may return Pascal-cased Id/Name/ExternalId.
          const assignedLinks = this.selectedProject.kubernetesClusters.map((s: any) =>
            this.normalizeListItem(s)
          );
          const assignedIds = new Set(assignedLinks.map((s: any) => s.id).filter(Boolean));

          // Items in the current page that are assigned: reuse their exact references
          // so ngx-datatable's reference-equality check marks them as checked.
          const assignedInPage = items.filter((item: any) => assignedIds.has(item.id));
          // Items assigned but not yet in this page: keep normalized links for saving.
          const notInPage = assignedLinks.filter((s: any) => !items.some((item: any) => item.id === s.id));
          this.selectedClusters = [...assignedInPage, ...notInPage];

          // Clear flag after a longer delay to allow datatable to fully initialize
          setTimeout(() => {
            this.isInitializingSelection = false;
          }, 1000);
        } else {
          this.selectedClusters = [];
        }

        // Show the paginated items
        this.setClusterData(items, response.totalCount || 0, true);
      },
      error: (err: any) => {
        this.loadClustersFallback();
      },
    });
  }

  private loadSubscriptionsFallback() {
    this.cloudAccountService.getCloudAccounts().subscribe({
      next: (accounts: any[]) => {
        const items = (Array.isArray(accounts) ? accounts : [])
          .map((item: any) => this.normalizeListItem(item))
          .filter((item: any) => item?.name || item?.externalId);
        this.setSubscriptionData(items, items.length, true);
      },
      error: () => {
        this.showToast(
          this.notificationConstants.warning as ToastStateName,
          this.notificationConstants.error,
          'Failed to load subscriptions'
        );
        this.showSubscriptionsTable = true;
      }
    });
  }

  private loadClustersFallback() {
    this.clusterService.getClusters().subscribe({
      next: (clusters: any) => {
        const items = (Array.isArray(clusters) ? clusters : [])
          .map((item: any) => this.normalizeListItem(item))
          .filter((item: any) => item?.name || item?.externalId);
        this.setClusterData(items, items.length, true);
      },
      error: () => {
        this.showToast(
          this.notificationConstants.warning as ToastStateName,
          this.notificationConstants.error,
          'Failed to load clusters'
        );
        this.showClustersTable = true;
      }
    });
  }

  /**
   * Search subscriptions by ID or name
   */
  public searchSubscriptions() {
    const searchTerm = this.subscriptionSearchTerm.trim();
    
    if (!searchTerm || searchTerm.length < 2) {
      this.showToast(
        this.notificationConstants.warning as ToastStateName,
        'Search Required',
        'Please enter at least 2 characters to search for subscriptions'
      );
      return;
    }

    this.isSearchingSubscriptions = true;
    this.subscriptionPageCache.clear();
    this.subsPageNumber = 0;
    
    this.cloudAccountService.getCloudAccountsPaginated(0, this.pageSize, searchTerm).subscribe({
      next: (response: any) => {
        let accounts = (response.items || [])
          .map((item: any) => this.normalizeListItem(item))
          .filter((item: any) => item.name || item.externalId);
        
        // In edit mode, replace matching items with selected references for proper highlighting
        if (this.isEditingProject && this.selectedSubscriptions.length > 0) {
          const selectedMap = new Map(this.selectedSubscriptions.map(s => [s.id, s]));
          accounts = accounts.map((item: any) => selectedMap.get(item.id) || item);
        }
        
        this.subscriptionRows = accounts;
        this.tempSubRows = accounts;
        this.totalSubscriptions = response.totalCount || 0;
        this.subscriptionPageCache.set(0, accounts);
        this.showSubscriptionsTable = true;
        this.isSearchingSubscriptions = false;

        if (accounts.length === 0) {
          this.showToast(
            this.notificationConstants.warning as ToastStateName,
            'No Results',
            'No subscriptions found matching your search'
          );
        }
      },
      error: (err: any) => {
        this.showToast(
          this.notificationConstants.warning as ToastStateName,
          this.notificationConstants.error,
          err.error || 'Failed to search subscriptions'
        );
        this.isSearchingSubscriptions = false;
      },
    });
  }

  /**
   * Handle subscription page change
   */
  public onSubscriptionPageChange(page: number) {
    this.subsPageNumber = page - 1;
    
    // If showing only selected items, paginate them client-side
    if (this.showOnlySelectedSubscriptions) {
      this.showSelectedSubscriptionsPage(this.subsPageNumber);
      return;
    }
    
    // Check cache first
    const cachedPage = this.subscriptionPageCache.get(this.subsPageNumber);
    if (cachedPage) {
      this.subscriptionRows = cachedPage;
      this.tempSubRows = cachedPage;
      return;
    }

    // Fetch from API if not cached
    this.isLoadingSubscriptions = true;
    const searchTerm = this.subscriptionSearchTerm.trim();
    this.cloudAccountService
      .getCloudAccountsPaginated(this.subsPageNumber, this.pageSize, searchTerm)
      .subscribe({
        next: (response: any) => {
          let items = (response.items || [])
            .map((item: any) => this.normalizeListItem(item))
            .filter((item: any) => item.name || item.externalId);
          
          // In edit mode, replace matching items with selected references for proper highlighting
          if (this.isEditingProject && this.selectedSubscriptions.length > 0) {
            const selectedMap = new Map(this.selectedSubscriptions.map(s => [s.id, s]));
            items = items.map((item: any) => selectedMap.get(item.id) || item);
          }
          
          this.subscriptionRows = items;
          this.tempSubRows = items;
          this.totalSubscriptions = response.totalCount || 0;
          // Cache this page
          this.subscriptionPageCache.set(this.subsPageNumber, items);
          this.isLoadingSubscriptions = false;
        },
        error: (err: any) => {
          this.showToast(
            this.notificationConstants.warning as ToastStateName,
            this.notificationConstants.error,
            err.error || 'Failed to load subscriptions'
          );
          this.isLoadingSubscriptions = false;
        },
      });
  }

  /**
   * Load subscriptions with pagination (fallback if search is not working)
   */
  public loadSubscriptionsPaginated() {
    this.isLoadingSubscriptions = true;
    this.cloudAccountService
      .getCloudAccountsPaginated(this.subsPageNumber, 50, this.subscriptionSearchTerm)
      .subscribe({
        next: (response: any) => {
          // Merge with already selected subscriptions
          const selectedIds = this.selectedSubscriptions.map((s: any) => s.id);
          const normalizedItems = (response.items || [])
            .map((item: any) => this.normalizeListItem(item))
            .filter((item: any) => item.name || item.externalId);
          const newAccounts = normalizedItems.filter(
            (acc: any) => !selectedIds.includes(acc.id)
          );
          
          this.subscriptionRows = [...this.selectedSubscriptions, ...newAccounts];
          this.tempSubRows = this.subscriptionRows;
          this.subscriptionTotalCount = response.totalCount || this.subscriptionRows.length;
          this.showSubscriptionsTable = true;
          this.isLoadingSubscriptions = false;
        },
        error: (err: any) => {
          this.showToast(
            this.notificationConstants.warning as ToastStateName,
            this.notificationConstants.error,
            err.error || 'Failed to load subscriptions'
          );
          this.isLoadingSubscriptions = false;
        },
      });
  }

  /**
   * Search clusters by ID or name
   */
  public searchClusters() {
    const searchTerm = this.clusterSearchTerm.trim();
    
    if (!searchTerm || searchTerm.length < 2) {
      this.showToast(
        this.notificationConstants.warning as ToastStateName,
        'Search Required',
        'Please enter at least 2 characters to search for clusters'
      );
      return;
    }

    this.isSearchingClusters = true;
    this.clusterPageCache.clear();
    this.clusPageNumber = 0;
    
    this.clusterService.getClustersPaginated(0, this.pageSize, searchTerm).subscribe({
      next: (response: any) => {
        let clusters = (response.items || [])
          .map((item: any) => this.normalizeListItem(item))
          .filter((item: any) => item.name || item.externalId);
        
        // In edit mode, replace matching items with selected references for proper highlighting
        if (this.isEditingProject && this.selectedClusters.length > 0) {
          const selectedMap = new Map(this.selectedClusters.map(s => [s.id, s]));
          clusters = clusters.map((item: any) => selectedMap.get(item.id) || item);
        }
        
        this.clusterRows = clusters;
        this.tempClusterRows = clusters;
        this.totalClusters = response.totalCount || 0;
        this.clusterPageCache.set(0, clusters);
        this.showClustersTable = true;
        this.isSearchingClusters = false;

        if (clusters.length === 0) {
          this.showToast(
            this.notificationConstants.warning as ToastStateName,
            'No Results',
            'No clusters found matching your search'
          );
        }
      },
      error: (err: any) => {
        this.showToast(
          this.notificationConstants.warning as ToastStateName,
          this.notificationConstants.error,
          err.error || 'Failed to search clusters'
        );
        this.isSearchingClusters = false;
      },
    });
  }

  /**
   * Handle cluster page change
   */
  public onClusterPageChange(page: number) {
    this.clusPageNumber = page - 1;
    
    // If showing only selected items, paginate them client-side
    if (this.showOnlySelectedClusters) {
      this.showSelectedClustersPage(this.clusPageNumber);
      return;
    }
    
    // Check cache first
    const cachedPage = this.clusterPageCache.get(this.clusPageNumber);
    if (cachedPage) {
      this.clusterRows = cachedPage;
      this.tempClusterRows = cachedPage;
      return;
    }

    // Fetch from API if not cached
    this.isLoadingClusters = true;
    const searchTerm = this.clusterSearchTerm.trim();
    this.clusterService
      .getClustersPaginated(this.clusPageNumber, this.pageSize, searchTerm)
      .subscribe({
        next: (response: any) => {
          let items = (response.items || [])
            .map((item: any) => this.normalizeListItem(item))
            .filter((item: any) => item.name || item.externalId);
          
          // In edit mode, replace matching items with selected references for proper highlighting
          if (this.isEditingProject && this.selectedClusters.length > 0) {
            const selectedMap = new Map(this.selectedClusters.map(s => [s.id, s]));
            items = items.map((item: any) => selectedMap.get(item.id) || item);
          }
          
          this.clusterRows = items;
          this.tempClusterRows = items;
          this.totalClusters = response.totalCount || 0;
          // Cache this page
          this.clusterPageCache.set(this.clusPageNumber, items);
          this.isLoadingClusters = false;
        },
        error: (err: any) => {
          this.showToast(
            this.notificationConstants.warning as ToastStateName,
            this.notificationConstants.error,
            err.error || 'Failed to load clusters'
          );
          this.isLoadingClusters = false;
        },
      });
  }

  /**
   * Load clusters with pagination (fallback if search is not working)
   */
  public loadClustersPaginated() {
    this.isLoadingClusters = true;
    this.clusterService
      .getClustersPaginated(this.clusPageNumber, 50, this.clusterSearchTerm)
      .subscribe({
        next: (response: any) => {
          // Merge with already selected clusters
          const selectedIds = this.selectedClusters.map((c: any) => c.id);
          const normalizedItems = (response.items || [])
            .map((item: any) => this.normalizeListItem(item))
            .filter((item: any) => item.name || item.externalId);
          const newClusters = normalizedItems.filter(
            (clus: any) => !selectedIds.includes(clus.id)
          );
          
          this.clusterRows = [...this.selectedClusters, ...newClusters];
          this.tempClusterRows = this.clusterRows;
          this.clusterTotalCount = response.totalCount || this.clusterRows.length;
          this.showClustersTable = true;
          this.isLoadingClusters = false;
        },
        error: (err: any) => {
          this.showToast(
            this.notificationConstants.warning as ToastStateName,
            this.notificationConstants.error,
            err.error || 'Failed to load clusters'
          );
          this.isLoadingClusters = false;
        },
      });
  }

  private buildForm() {
    return new FormGroup({
      name: new FormControl('', [Validators.required, Validators.minLength(3), preventSagPrefixValidator]),
      //description: new FormControl(''),
      businessUnit: new FormControl(''),
      newProjectOwner: new FormControl(''),
      entraGroupObjectId: new FormControl(''),
      subscriptions: new FormControl(['']),
      isFolder: new FormControl(),
    });
  }

  private isValidGuid(value: string): boolean {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      value.trim()
    );
  }

  public AddOwnerToProject() {
    this.isAddingOwner = true;
    let email = this.createProjectForm.value.newProjectOwner!;

    // Security: Validate that email doesn't contain HTML tags
    if (this.containsHtmlTags(email)) {
      this.showToast(
        this.notificationConstants.warning as ToastStateName,
        this.notificationConstants.error,
        'Email address cannot contain HTML tags. Please enter a valid email address.'
      );
      this.isAddingOwner = false;
      return;
    }

    // Security: Sanitize email input
    email = this.sanitizeInput(email);
    
    const index: number = this.newProjectOwners.indexOf(email);

    // Check if User exists already in the current List.
    if (index > -1) {
      this.showToast(
        this.notificationConstants.warning as ToastStateName,
        this.notificationConstants.duplicateOwner,
        this.notificationConstants.userAlreadyExists
      );
      this.createProjectForm.controls.newProjectOwner.reset();
      this.isAddingOwner = false;
      return;
    }

    // Check if input is in Email format.
    if (
      !this.isInputEmailFormat(email)
    ) {
      this.showToast(
        this.notificationConstants.warning as ToastStateName,
        this.notificationConstants.invalidEmail,
        this.notificationConstants.inputNotValid
      );
      this.isAddingOwner = false;
      return;
    }

    // Finally Check if the email exists for a user on Wiz.
    this.userService
      .ensureSamlUser(email)
      .pipe(finalize(() => {
        this.isAddingOwner = false;
        this.cdr.detectChanges();
      }))
      .subscribe({
        next: (wizProjectOwner: any) => {
          if (wizProjectOwner?.email) {
            // Security: Store sanitized email
            this.newProjectOwners = [
              ...this.newProjectOwners,
              this.sanitizeInput(wizProjectOwner.email),
            ];
            this.cdr.detectChanges();
          } else {
            this.showToast(
              this.notificationConstants.warning as ToastStateName,
              this.notificationConstants.userNotFound,
              this.notificationConstants.unableToFindUser
            );
          }
        },
        error: (err: any) => {
          const errorTitle =
            err?.error?.title ||
            err?.statusText ||
            this.notificationConstants.error;
          const errorMessage =
            err?.error?.detail ||
            err?.error?.message ||
            err?.message ||
            this.notificationConstants.unableToFindUser;

          this.showToast(
            this.notificationConstants.warning as ToastStateName,
            errorTitle,
            errorMessage
          );
        },
      });

    // Reset the Form input.
    this.createProjectForm.controls.newProjectOwner.reset();
  }

  public RemoveFromNewOwners(email: string) {
    if (email !== this.currentUser?.email) {
      const index: number = this.newProjectOwners.indexOf(email);
      if (index !== -1) {
        this.newProjectOwners.splice(index, 1);
      }
    } else {
      this.showToast(
        this.notificationConstants.warning as ToastStateName,
        this.notificationConstants.unableToRemoveOwners,
        this.notificationConstants.cantRemove
      );
    }
  }

  public setFolder() {
    this.isFolderProject = !this.isFolderProject;
    this.selectedFolder = 'Root';
  }

  public searchSubscriptionsFromInput() {
    const searchTerm = this.subscriptionSearchTerm.trim();
    if (searchTerm) {
      this.searchSubscriptions();
    }
  }

  public clearSubscriptionSearch() {
    this.subscriptionSearchTerm = '';
    this.isSearchingSubscriptions = false;
    this.subscriptionPageCache.clear();
    this.subsPageNumber = 0;
    this.showOnlySelectedSubscriptions = false;
    // Reload initial data
    this.loadInitialSubscriptions();
  }

  public toggleShowOnlySelectedSubscriptions() {
    this.showOnlySelectedSubscriptions = !this.showOnlySelectedSubscriptions;
    
    if (this.showOnlySelectedSubscriptions) {
      // Show only selected subscriptions (client-side pagination)
      if (this.selectedSubscriptions.length === 0) {
        this.subscriptionRows = [];
        this.tempSubRows = [];
        return;
      }
      
      // Show first page of selected items (10 per page)
      this.subsPageNumber = 0;
      this.showSelectedSubscriptionsPage(0);
    } else {
      // Restore cached page or reload
      this.subsPageNumber = 0;
      const cachedPage = this.subscriptionPageCache.get(0);
      if (cachedPage) {
        this.subscriptionRows = cachedPage;
        this.tempSubRows = cachedPage;
      } else {
        this.loadInitialSubscriptions();
      }
    }
  }

  private showSelectedSubscriptionsPage(pageIndex: number) {
    const startIndex = pageIndex * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    this.subscriptionRows = this.selectedSubscriptions.slice(startIndex, endIndex);
    this.tempSubRows = this.subscriptionRows;
  }

  public updateSubscriptionFilter(event: any) {
    const val = event.target.value.toLowerCase();

    // filter our data (client-side filtering of search results)
    const tempProjects = this.tempSubRows.filter(
      (e: any) =>
        e.externalId.toLowerCase().includes(val) ||
        e.name.toLowerCase().includes(val) ||
        !val
    );

    // update the subscriptions
    this.subscriptionRows = tempProjects;

    // Whenever the filter changes, always go back to the first page
    this.subsPageNumber = 0;
  }

  public searchClustersFromInput() {
    const searchTerm = this.clusterSearchTerm.trim();
    if (searchTerm) {
      this.searchClusters();
    }
  }

  public clearClusterSearch() {
    this.clusterSearchTerm = '';
    this.isSearchingClusters = false;
    this.clusterPageCache.clear();
    this.clusPageNumber = 0;
    this.showOnlySelectedClusters = false;
    // Reload initial data
    this.loadInitialClusters();
  }

  public toggleShowOnlySelectedClusters() {
    this.showOnlySelectedClusters = !this.showOnlySelectedClusters;
    
    if (this.showOnlySelectedClusters) {
      // Show only selected clusters (client-side pagination)
      if (this.selectedClusters.length === 0) {
        this.clusterRows = [];
        this.tempClusterRows = [];
        return;
      }
      
      // Show first page of selected items (10 per page)
      this.clusPageNumber = 0;
      this.showSelectedClustersPage(0);
    } else {
      // Restore cached page or reload
      this.clusPageNumber = 0;
      const cachedPage = this.clusterPageCache.get(0);
      if (cachedPage) {
        this.clusterRows = cachedPage;
        this.tempClusterRows = cachedPage;
      } else {
        this.loadInitialClusters();
      }
    }
  }

  private showSelectedClustersPage(pageIndex: number) {
    const startIndex = pageIndex * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    this.clusterRows = this.selectedClusters.slice(startIndex, endIndex);
    this.tempClusterRows = this.clusterRows;
  }

  public updateClusterFilter(event: any) {
    const val = event.target.value.toLowerCase();

    // filter our data (client-side filtering of search results)
    const tempClusters = this.tempClusterRows.filter(
      (e: any) =>
        e.externalId.toLowerCase().includes(val) ||
        e.name.toLowerCase().includes(val) ||
        !val
    );

    // update the clusters
    this.clusterRows = tempClusters;

    // Whenever the filter changes, always go back to the first page
    this.clusPageNumber = 0;
  }

  public onSelectSubscriptions({ selected }: any) {
    // Don't overwrite selection during initialization in edit mode
    if (this.isInitializingSelection) {
      return;
    }
    // Update subscription selection
    this.selectedSubscriptions = [...selected];
  }

  public onSelect({ selected }: any) {
    // Don't overwrite selection during initialization in edit mode
    if (this.isInitializingSelection) {
      return;
    }
    // Update cluster selection
    this.selectedClusters = [...selected];
  }

  private isValid(): boolean {
    return !this.createProjectForm || this.createProjectForm.valid;
  }

  public save() {
    if (!this.isValid()) {
      return;
    }

    const entraGroupObjectId = String(
      this.createProjectForm.value.entraGroupObjectId ?? ''
    ).trim();

    if (entraGroupObjectId && !this.isValidGuid(entraGroupObjectId)) {
      this.showToast(
        this.notificationConstants.warning as ToastStateName,
        this.notificationConstants.invalidInput,
        this.constants.invalidEntraGroupId
      );
      return;
    }

    // Security: Validate project name does not start with "SAG"
    const projectName = this.createProjectForm.value.name;
    if (projectName && projectName.toString().trim().toUpperCase().startsWith('SAG')) {
      this.showToast(
        this.notificationConstants.warning as ToastStateName,
        this.notificationConstants.error,
        'Project name cannot start with "SAG". Please choose a different name.'
      );
      return;
    }

    // Security: Validate inputs do not contain HTML tags
    const businessUnit = this.createProjectForm.value.businessUnit;
    const unsavedOwnerInput = this.createProjectForm.value.newProjectOwner;

    // Security: Check if there's unsaved owner input with HTML tags
    if (unsavedOwnerInput && unsavedOwnerInput.trim() !== '') {
      if (this.containsHtmlTags(unsavedOwnerInput)) {
        this.showToast(
          this.notificationConstants.warning as ToastStateName,
          this.notificationConstants.error,
          'Owner email field contains HTML tags. Please clear the field or click Add button first.'
        );
        return;
      }
      // Warn user about unsaved owner
      this.showToast(
        this.notificationConstants.warning as ToastStateName,
        this.notificationConstants.error,
        'You have an unsaved owner email. Please click Add button or clear the field before saving.'
      );
      return;
    }

    if (this.containsHtmlTags(projectName)) {
      this.showToast(
        this.notificationConstants.warning as ToastStateName,
        this.notificationConstants.error,
        'Project name cannot contain HTML tags. Please remove any < or > characters.'
      );
      return;
    }

    if (this.containsHtmlTags(businessUnit)) {
      this.showToast(
        this.notificationConstants.warning as ToastStateName,
        this.notificationConstants.error,
        'Business Unit cannot contain HTML tags. Please remove any < or > characters.'
      );
      return;
    }

    if (
      this.selectedSubscriptions?.length === 0 &&
      this.selectedClusters?.length === 0 &&
      !this.isFolderProject
    ) {
      this.showToast(
        this.notificationConstants.warning as ToastStateName,
        this.notificationConstants.noSubscriptionOrClusters,
        this.notificationConstants.selectSubscriptionOrCluster
      );
      return;
    }

    this.isLoading = true;

    let projectInput = new ProjectInput();

    // Security: Sanitize all text inputs before sending to backend
    projectInput.name = this.sanitizeInput(projectName);
    projectInput.businessUnit = this.sanitizeInput(businessUnit);
    // Security: Sanitize all owner emails
    projectInput.projectOwners = this.newProjectOwners.map(owner => this.sanitizeInput(owner));
    projectInput.isFolder =
      this.createProjectForm.value.isFolder === true ? true : false;
    projectInput.cloudAccountLinks = this.isFolderProject
      ? []
      : this.CreateCloudAccountType(this.selectedSubscriptions!);
    projectInput.kubernetesClusterSetLinks = this.isFolderProject
      ? []
      : this.createClusterType(this.selectedClusters);
    projectInput.parentProjectId =
      this.selectedFolder == 'Root' ? '' : this.selectedFolder;

    this.projectService.createProject(projectInput).subscribe({
      next: (newProject) => {
        if (newProject !== null) {
          const createdProjectId = newProject?.id;

          if (entraGroupObjectId && createdProjectId) {
            this.userService.addEntraGroupToProject(createdProjectId, entraGroupObjectId).subscribe({
              next: () => {
                this.ref?.hide();
                this.showToast(
                  this.notificationConstants.success as ToastStateName,
                  this.notificationConstants.successCap,
                  this.notificationConstants.project +
                    projectInput.name +
                    this.notificationConstants.created
                );
                this.onSave.emit(true);
              },
              error: (err: any) => {
                this.ref?.hide();
                this.showToast(
                  this.notificationConstants.success as ToastStateName,
                  this.notificationConstants.successCap,
                  this.notificationConstants.project +
                    projectInput.name +
                    this.notificationConstants.created
                );
                this.showToast(
                  this.notificationConstants.warning as ToastStateName,
                  this.notificationConstants.error,
                  err?.error?.detail ||
                    err?.error?.message ||
                    'Project was created, but the Entra ID Group mapping could not be added.'
                );
                this.onSave.emit(true);
              },
            });
            return;
          }

          this.ref?.hide();
          this.showToast(
            this.notificationConstants.success as ToastStateName,
            this.notificationConstants.successCap,
            this.notificationConstants.project +
              projectInput.name +
              this.notificationConstants.created
          );
          this.onSave.emit(true);
        } else {
          this.isLoading = false;
          this.showToast(
            this.notificationConstants.danger as ToastStateName,
            this.notificationConstants.error,
            this.notificationConstants.unableToCreateProject
          );
        }
      },
      error: (err: any) => {
        if (err.status === 503) {
          this.isLoading = true;

          this.projectService
            .getProjectsByName(projectInput.name)
            .pipe(
              switchMap((response: any) => {
                // Check if expected project is present or response is valid
                const isValid = response && response.length > 0;

                if (!isValid) {
                  // Force retry by throwing an error, even though status is 200
                  return throwError(
                    () => new Error('Invalid response or project not found')
                  );
                }

                // Pass the valid response along
                return of(response);
              }),
              retryWhen((errors) =>
                errors.pipe(
                  scan((retryCount, err) => {
                    if (retryCount >= 2) {
                      throw err;
                    }
                    return retryCount + 1;
                  }, 0),
                  delay(5000) // wait 5 seconds before each retry
                )
              ),
              catchError((retryErr: any) => {
                this.isLoading = false;
                this.showToast(
                  this.notificationConstants.danger as ToastStateName,
                  this.notificationConstants.error,
                  this.notificationConstants.failedToLoad
                );
                return of(null); // Return fallback observable
              })
            )
            .subscribe({
              next: (response: any) => {
                if (response) {
                  this.showToast(
                    this.notificationConstants.success as ToastStateName,
                    this.notificationConstants.success,
                    this.notificationConstants.projectCreatedSuccess
                  );
                }
                this.isLoading = false;
              },
            });
        } else {
          this.isLoading = false;
          this.showToast(
            this.notificationConstants.danger as ToastStateName,
            this.notificationConstants.error,
            err.error['detail']
          );
        }
      },
    });
    this.createProjectForm.reset();
  }

  public update() {
    if (!this.isValid()) {
      return;
    }

    // Security: Validate project name does not start with "SAG"
    const projectName = this.createProjectForm.value.name;
    if (projectName && projectName.toString().trim().toUpperCase().startsWith('SAG')) {
      this.showToast(
        this.notificationConstants.warning as ToastStateName,
        this.notificationConstants.error,
        'Project name cannot start with "SAG". Please choose a different name.'
      );
      return;
    }

    // Security: Validate inputs do not contain HTML tags
    const businessUnit = this.createProjectForm.value.businessUnit;
    const unsavedOwnerInput = this.createProjectForm.value.newProjectOwner;

    // Security: Check if there's unsaved owner input with HTML tags
    if (unsavedOwnerInput && unsavedOwnerInput.trim() !== '') {
      if (this.containsHtmlTags(unsavedOwnerInput)) {
        this.showToast(
          this.notificationConstants.warning as ToastStateName,
          this.notificationConstants.error,
          'Owner email field contains HTML tags. Please clear the field or click Add button first.'
        );
        return;
      }
      // Warn user about unsaved owner
      this.showToast(
        this.notificationConstants.warning as ToastStateName,
        this.notificationConstants.error,
        'You have an unsaved owner email. Please click Add button or clear the field before saving.'
      );
      return;
    }

    if (this.containsHtmlTags(projectName)) {
      this.showToast(
        this.notificationConstants.warning as ToastStateName,
        this.notificationConstants.error,
        'Project name cannot contain HTML tags. Please remove any < or > characters.'
      );
      return;
    }

    if (this.containsHtmlTags(businessUnit)) {
      this.showToast(
        this.notificationConstants.warning as ToastStateName,
        this.notificationConstants.error,
        'Business Unit cannot contain HTML tags. Please remove any < or > characters.'
      );
      return;
    }

    this.isLoading = true;

    let projectInput = new ProjectInput();
    projectInput.id = this.selectedProject?.id;
    projectInput.identifiers = this.selectedProject?.identifiers;
    // Security: Sanitize all text inputs before sending to backend
    projectInput.name = this.sanitizeInput(projectName);
    projectInput.businessUnit = this.sanitizeInput(businessUnit);
    // Security: Sanitize all owner emails
    projectInput.projectOwners = this.newProjectOwners.map(owner => this.sanitizeInput(owner));
    projectInput.isFolder = this.selectedProject?.isFolder;
    projectInput.parentProjectId =
      this.selectedFolder == 'Root' ? '' : this.selectedFolder;
    if (!this.selectedProject?.isFolder) {
      projectInput.cloudAccountLinks = this.CreateCloudAccountType(
        this.selectedSubscriptions!
      );
      projectInput.kubernetesClusterSetLinks = this.createClusterType(
        this.selectedClusters
      );
    }

    this.projectService.updateProject(projectInput).subscribe({
      next: (newProject) => {
        if (newProject !== null) {
          const updatedProject = {
            ...this.selectedProject,
            ...newProject,
            ...projectInput,
            cloudAccountLinks: [...this.selectedSubscriptions],
            kubernetesClusters: [...this.selectedClusters],
            subscriptionCount: this.selectedSubscriptions.length,
            kubernetesClusterCount: this.selectedClusters.length,
          };

          this.ref?.hide();
          this.showToast(
            this.notificationConstants.success as ToastStateName,
            this.notificationConstants.successCap,
            this.notificationConstants.project +
              projectInput.name +
              this.notificationConstants.updated
          );
          this.onSave.emit({
            changed: true,
            changeType: 'updated',
            project: updatedProject,
            totalSubscriptions: updatedProject.subscriptionCount,
            totalClusters: updatedProject.kubernetesClusterCount,
          });
        } else {
          this.isLoading = false;
          this.showToast(
            this.notificationConstants.danger as ToastStateName,
            this.notificationConstants.error,
            this.notificationConstants.unableToUpdateProject
          );
        }
      },
      error: (err) => {
        this.isLoading = false;
        this.showToast(
          this.notificationConstants.danger as ToastStateName,
          this.notificationConstants.error,
          err.error['detail']
        );
      },
    });
  }

  public cancel() {
    this.createProjectForm.reset();
    this.onClose.emit(true);
  }

  private CreateCloudAccountType(subs: CloudAccount[]): CloudAccountInput[] {
    return subs.map((sub) => {
      const account = new CloudAccountInput();
      account.cloudAccount = sub.id;
      return account;
    });
  }

  private createClusterType(clusters: Cluster[]) {
    const account = new ClusterInput();
    account.kubernetesClusters = [];
    clusters.forEach((cluster) => {
      account.kubernetesClusters.push(cluster.id);
    });
    return [account];
  }

  public onCloseDialog() {
    this.onClose.emit(true);
  }

  private isInputEmailFormat(email: string) {
    const regexExp =
      /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/gi;
    return regexExp.test(email);
  }

  /**
   * Sanitizes text input by removing HTML tags and potentially dangerous characters.
   * This provides defense-in-depth protection against HTML injection attacks.
   * @param input - The text to sanitize
   * @returns Sanitized text with HTML tags and dangerous characters removed
   */
  private sanitizeInput(input: string | undefined | null): string {
    if (!input) {
      return '';
    }
    
    // Remove HTML tags using regex
    let sanitized = input.replace(/<[^>]*>/g, '');
    
    // Remove potentially dangerous characters and scripts
    sanitized = sanitized
      .replace(/javascript:/gi, '')
      .replace(/on\w+\s*=/gi, '')
      .replace(/&lt;/gi, '')
      .replace(/&gt;/gi, '')
      .replace(/&amp;/gi, '&');
    
    // Trim whitespace
    return sanitized.trim();
  }

  /**
   * Validates that input does not contain HTML tags.
   * @param input - The text to validate
   * @returns true if input contains HTML tags, false otherwise
   */
  private containsHtmlTags(input: string | undefined | null): boolean {
    if (!input) {
      return false;
    }
    const htmlTagPattern = /<[^>]*>/;
    return htmlTagPattern.test(input);
  }

  private showToast(
    state: ToastStateName,
    title: string,
    message: string,
    disableAutoClose?: boolean,
    disableManualClose?: boolean,
    timeout?: number,
    action?: Link
  ) {
    this.toastNotificationService.showToastNotification({
      state,
      title,
      message,
      disableAutoClose,
      disableManualClose,
      timeout,
      action,
    });
  }
}
